// pages/OrganizationSetup.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Slider,
  LinearProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  IconButton
} from '@mui/material';
import {
  Business,
  Group,
  AttachMoney,
  Timeline,
  Psychology,
  TrendingUp,
  CheckCircle,
  ArrowForward,
  ArrowBack,
  RocketLaunch,
  Assessment,
  AutoGraph
} from '@mui/icons-material';
import { useIEP } from '../contexts/IEPContext';
import { useNavigate } from 'react-router-dom'; // ✅ ADDED

const OrganizationSetup = () => { // ✅ REMOVED onSetup prop
  const navigate = useNavigate(); // ✅ ADDED
  const { updateOrganization } = useIEP(); // ✅ ADDED
  const [activeStep, setActiveStep] = useState(0);
  const [organization, setOrganization] = useState({
    name: '',
    industry: '',
    size: '',
    revenue: '',
    maturity: 30,
    strategicFocus: '',
    challenges: [],
    resources: {
      budget: 500000,
      teamSize: 5,
      timeline: 180
    }
  });
  const [errors, setErrors] = useState({});

  const steps = [
    'Basic Information',
    'Industry & Size',
    'Current State',
    'Challenges',
    'Resources',
    'Review & Complete'
  ];

  const industries = [
    'Technology',
    'Healthcare',
    'Financial Services',
    'Manufacturing',
    'Retail',
    'Education',
    'Professional Services',
    'Non-Profit',
    'Government',
    'Energy',
    'Telecommunications',
    'Transportation',
    'Media & Entertainment',
    'Real Estate',
    'Agriculture',
    'Other'
  ];

  const companySizes = [
    'Startup (1-10 employees)',
    'Small (11-50 employees)',
    'Medium (51-250 employees)',
    'Large (251-1000 employees)',
    'Enterprise (1000+ employees)'
  ];

  const revenueRanges = [
    'Less than $1M',
    '$1M - $10M',
    '$10M - $50M',
    '$50M - $100M',
    '$100M - $500M',
    '$500M - $1B',
    '$1B+'
  ];

  const strategicFocusOptions = [
    'Growth & Expansion',
    'Cost Optimization',
    'Digital Transformation',
    'Innovation & R&D',
    'Customer Experience',
    'Operational Excellence',
    'Sustainability',
    'Risk Management',
    'Talent Development',
    'Market Leadership'
  ];

  const commonChallenges = [
    'Scaling operations',
    'Digital transformation',
    'Innovation stagnation',
    'Customer retention',
    'Cost management',
    'Talent acquisition',
    'Market competition',
    'Technology debt',
    'Process inefficiency',
    'Strategic alignment',
    'Culture development',
    'Risk management',
    'Sustainability goals',
    'Regulatory compliance',
    'Data-driven decisions'
  ];

  const validateStep = (step) => {
    const newErrors = {};
    
    switch(step) {
      case 0:
        if (!organization.name) newErrors.name = 'Organization name is required';
        break;
      case 1:
        if (!organization.industry) newErrors.industry = 'Industry is required';
        if (!organization.size) newErrors.size = 'Company size is required';
        break;
      case 2:
        if (!organization.revenue) newErrors.revenue = 'Revenue range is required';
        if (!organization.strategicFocus) newErrors.strategicFocus = 'Strategic focus is required';
        break;
      case 3:
        if (organization.challenges.length === 0) newErrors.challenges = 'Select at least one challenge';
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      if (activeStep === steps.length - 1) {
        completeSetup();
      } else {
        setActiveStep(activeStep + 1);
      }
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleChange = (field, value) => {
    setOrganization(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleResourceChange = (field, value) => {
    setOrganization(prev => ({
      ...prev,
      resources: {
        ...prev.resources,
        [field]: value
      }
    }));
  };

  const toggleChallenge = (challenge) => {
    setOrganization(prev => ({
      ...prev,
      challenges: prev.challenges.includes(challenge)
        ? prev.challenges.filter(c => c !== challenge)
        : [...prev.challenges, challenge]
    }));
  };

  const completeSetup = () => {
    // Add generated ID and timestamp
    const completeOrg = {
      ...organization,
      id: `org_${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    // ✅ FIXED: Use context update function instead of prop
    if (updateOrganization) {
      updateOrganization(completeOrg);
    }
    
    // ✅ Navigate to dashboard
    navigate('/iep/dashboard');
  };

  const calculateMaturityLevel = (score) => {
    if (score >= 80) return 'Advanced';
    if (score >= 60) return 'Developing';
    if (score >= 40) return 'Intermediate';
    return 'Foundational';
  };

  const getIndustryIcon = (industry) => {
    const icons = {
      'Technology': '💻',
      'Healthcare': '🏥',
      'Financial Services': '💰',
      'Manufacturing': '🏭',
      'Retail': '🛍️',
      'Education': '🎓',
      'Professional Services': '👔',
      'Non-Profit': '🤝',
      'Government': '🏛️',
      'Energy': '⚡',
      'Telecommunications': '📡',
      'Transportation': '🚚',
      'Media & Entertainment': '🎬',
      'Real Estate': '🏠',
      'Agriculture': '🌾',
      'Other': '🏢'
    };
    return icons[industry] || '🏢';
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box textAlign="center" mb={4}>
        <Typography variant="h1" gutterBottom>
          <RocketLaunch sx={{ verticalAlign: 'middle', mr: 2, fontSize: 'inherit' }} />
          Welcome to IEP Transformation Platform
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Let's set up your organization profile for personalized transformation recommendations
        </Typography>
      </Box>

      {/* Progress */}
      <Stepper activeStep={activeStep} sx={{ mb: 6 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 1: Basic Information */}
      {activeStep === 0 && (
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              <Business sx={{ verticalAlign: 'middle', mr: 1 }} />
              Basic Information
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Tell us about your organization
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Organization Name"
                  value={organization.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Website URL (optional)"
                  placeholder="https://example.com"
                  value={organization.website || ''}
                  onChange={(e) => handleChange('website', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Location (optional)"
                  placeholder="City, Country"
                  value={organization.location || ''}
                  onChange={(e) => handleChange('location', e.target.value)}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Industry & Size */}
      {activeStep === 1 && (
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              <Group sx={{ verticalAlign: 'middle', mr: 1 }} />
              Industry & Size
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Select your industry and company size
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.industry}>
                  <InputLabel>Industry *</InputLabel>
                  <Select
                    value={organization.industry}
                    label="Industry *"
                    onChange={(e) => handleChange('industry', e.target.value)}
                  >
                    {industries.map(industry => (
                      <MenuItem key={industry} value={industry}>
                        <Box display="flex" alignItems="center">
                          <Typography sx={{ mr: 2, fontSize: '1.2rem' }}>
                            {getIndustryIcon(industry)}
                          </Typography>
                          {industry}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.industry && (
                    <Typography variant="caption" color="error">
                      {errors.industry}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.size}>
                  <InputLabel>Company Size *</InputLabel>
                  <Select
                    value={organization.size}
                    label="Company Size *"
                    onChange={(e) => handleChange('size', e.target.value)}
                  >
                    {companySizes.map(size => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.size && (
                    <Typography variant="caption" color="error">
                      {errors.size}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom mt={2}>
                  Years in Operation
                </Typography>
                <Slider
                  value={organization.years || 5}
                  onChange={(e, value) => handleChange('years', value)}
                  min={0}
                  max={50}
                  step={1}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 10, label: '10' },
                    { value: 20, label: '20' },
                    { value: 30, label: '30' },
                    { value: 40, label: '40' },
                    { value: 50, label: '50+' }
                  ]}
                  valueLabelDisplay="auto"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Current State */}
      {activeStep === 2 && (
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              <Assessment sx={{ verticalAlign: 'middle', mr: 1 }} />
              Current State Assessment
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Help us understand your current position
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.revenue}>
                  <InputLabel>Annual Revenue *</InputLabel>
                  <Select
                    value={organization.revenue}
                    label="Annual Revenue *"
                    onChange={(e) => handleChange('revenue', e.target.value)}
                  >
                    {revenueRanges.map(range => (
                      <MenuItem key={range} value={range}>
                        {range}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.revenue && (
                    <Typography variant="caption" color="error">
                      {errors.revenue}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.strategicFocus}>
                  <InputLabel>Primary Strategic Focus *</InputLabel>
                  <Select
                    value={organization.strategicFocus}
                    label="Primary Strategic Focus *"
                    onChange={(e) => handleChange('strategicFocus', e.target.value)}
                  >
                    {strategicFocusOptions.map(focus => (
                      <MenuItem key={focus} value={focus}>
                        {focus}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.strategicFocus && (
                    <Typography variant="caption" color="error">
                      {errors.strategicFocus}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom mt={2}>
                  Current Organizational Maturity: {organization.maturity}/100
                </Typography>
                <Box display="flex" alignItems="center">
                  <Slider
                    value={organization.maturity}
                    onChange={(e, value) => handleChange('maturity', value)}
                    min={0}
                    max={100}
                    step={5}
                    sx={{ flexGrow: 1, mr: 3 }}
                  />
                  <Chip 
                    label={calculateMaturityLevel(organization.maturity)}
                    color={
                      organization.maturity >= 80 ? 'success' :
                      organization.maturity >= 60 ? 'primary' :
                      organization.maturity >= 40 ? 'warning' : 'error'
                    }
                  />
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={organization.maturity}
                  sx={{ mt: 1, height: 6, borderRadius: 3 }}
                  color={
                    organization.maturity >= 80 ? 'success' :
                    organization.maturity >= 60 ? 'primary' :
                    organization.maturity >= 40 ? 'warning' : 'error'
                  }
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Challenges */}
      {activeStep === 3 && (
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              <Psychology sx={{ verticalAlign: 'middle', mr: 1 }} />
              Key Challenges
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Select your top challenges (select 3-5)
            </Typography>
            
            {errors.challenges && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.challenges}
              </Alert>
            )}
            
            <Grid container spacing={2}>
              {commonChallenges.map((challenge) => (
                <Grid item xs={12} sm={6} md={4} key={challenge}>
                  <Paper
                    elevation={organization.challenges.includes(challenge) ? 3 : 1}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      border: organization.challenges.includes(challenge) ? '2px solid #6366f1' : '1px solid #334155',
                      borderRadius: 2,
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => toggleChallenge(challenge)}
                  >
                    <Box display="flex" alignItems="center">
                      <Checkbox
                        checked={organization.challenges.includes(challenge)}
                        sx={{ mr: 1 }}
                      />
                      <Typography variant="body2">
                        {challenge}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            
            <Box mt={3}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Challenges ({organization.challenges.length}):
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {organization.challenges.map(challenge => (
                  <Chip 
                    key={challenge}
                    label={challenge}
                    onDelete={() => toggleChallenge(challenge)}
                  />
                ))}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Step 5: Resources */}
      {activeStep === 4 && (
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              <AttachMoney sx={{ verticalAlign: 'middle', mr: 1 }} />
              Available Resources
            </Typography>
            <Typography variant="body2" color="text-secondary" paragraph>
              Configure your transformation budget and resources
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography gutterBottom>
                  Transformation Budget: ${(organization.resources.budget / 1000).toFixed(0)}K
                </Typography>
                <Slider
                  value={organization.resources.budget}
                  onChange={(e, value) => handleResourceChange('budget', value)}
                  min={100000}
                  max={5000000}
                  step={100000}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `$${(value / 1000).toFixed(0)}K`}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography gutterBottom>
                  Available Team Size: {organization.resources.teamSize} members
                </Typography>
                <Slider
                  value={organization.resources.teamSize}
                  onChange={(e, value) => handleResourceChange('teamSize', value)}
                  min={1}
                  max={20}
                  step={1}
                  valueLabelDisplay="auto"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography gutterBottom>
                  Preferred Timeline: {organization.resources.timeline} days
                </Typography>
                <Slider
                  value={organization.resources.timeline}
                  onChange={(e, value) => handleResourceChange('timeline', value)}
                  min={90}
                  max={730}
                  step={30}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value} days`}
                />
              </Grid>
            </Grid>
            
            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                These constraints will help our AI engine generate the most appropriate 
                transformation roadmap for your organization.
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Step 6: Review & Complete */}
      {activeStep === 5 && (
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              <CheckCircle sx={{ verticalAlign: 'middle', mr: 1, color: 'success.main' }} />
              Review & Complete Setup
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Review your information before we generate your personalized transformation plan
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 3 }}>
                  <Typography variant="subtitle1" gutterBottom color="primary">
                    Organization Details
                  </Typography>
                  
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Organization Name"
                        secondary={organization.name}
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemText 
                        primary="Industry"
                        secondary={
                          <Box display="flex" alignItems="center">
                            <Typography sx={{ mr: 1 }}>
                              {getIndustryIcon(organization.industry)}
                            </Typography>
                            {organization.industry}
                          </Box>
                        }
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemText 
                        primary="Company Size"
                        secondary={organization.size}
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemText 
                        primary="Annual Revenue"
                        secondary={organization.revenue}
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemText 
                        primary="Strategic Focus"
                        secondary={organization.strategicFocus}
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 3 }}>
                  <Typography variant="subtitle1" gutterBottom color="primary">
                    Transformation Profile
                  </Typography>
                  
                  <Box mb={3}>
                    <Typography variant="subtitle2" gutterBottom>
                      Maturity Level
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <LinearProgress 
                        variant="determinate" 
                        value={organization.maturity}
                        sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
                        color={
                          organization.maturity >= 80 ? 'success' :
                          organization.maturity >= 60 ? 'primary' :
                          organization.maturity >= 40 ? 'warning' : 'error'
                        }
                      />
                      <Typography variant="h6" sx={{ ml: 2 }}>
                        {organization.maturity}/100
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {calculateMaturityLevel(organization.maturity)}
                    </Typography>
                  </Box>
                  
                  <Box mb={3}>
                    <Typography variant="subtitle2" gutterBottom>
                      Key Challenges ({organization.challenges.length})
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {organization.challenges.map(challenge => (
                        <Chip 
                          key={challenge}
                          label={challenge}
                          size="small"
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Available Resources
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            Budget
                          </Typography>
                          <Typography variant="body2">
                            ${(organization.resources.budget / 1000).toFixed(0)}K
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={4}>
                        <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            Team
                          </Typography>
                          <Typography variant="body2">
                            {organization.resources.teamSize} members
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={4}>
                        <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            Timeline
                          </Typography>
                          <Typography variant="body2">
                            {organization.resources.timeline} days
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
            
            <Alert severity="success" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Ready to transform!</strong> Based on your profile, our AI will generate 
                a personalized transformation roadmap with {Object.keys(require('../data/IEPMasterData_Final.json').transformations).length} 
                possible transformations optimized for your specific needs.
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Navigation Buttons */}
      <Box display="flex" justifyContent="space-between" mt={4}>
        <Button
          onClick={handleBack}
          disabled={activeStep === 0}
          startIcon={<ArrowBack />}
        >
          Back
        </Button>
        
        <Button
          variant="contained"
          onClick={handleNext}
          endIcon={activeStep === steps.length - 1 ? <RocketLaunch /> : <ArrowForward />}
        >
          {activeStep === steps.length - 1 ? 'Complete Setup & Generate Roadmap' : 'Continue'}
        </Button>
      </Box>

      {/* Progress Indicator */}
      <Box mt={3}>
        <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
          Step {activeStep + 1} of {steps.length} • {Math.round((activeStep + 1) / steps.length * 100)}% complete
        </Typography>
        <LinearProgress 
          variant="determinate" 
          value={(activeStep + 1) / steps.length * 100}
          sx={{ mt: 1, height: 4, borderRadius: 2 }}
        />
      </Box>
    </Box>
  );
};

export default OrganizationSetup;